import React, { useCallback, useMemo } from "react";

import { faCheck } from "@fortawesome/pro-regular-svg-icons/faCheck";
import { faEllipsisH } from "@fortawesome/pro-solid-svg-icons/faEllipsisH";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { MenuProps } from "antd/es/menu";
import { useIntl } from "react-intl";

import { Dropdown, ListWithOverflowItem } from "@mapmycustomers/ui";

import RoundButton from "@app/component/RoundButton";

import GlobalSearchFilter, {
  getGlobalSearchFilterDisplayName,
} from "../../enum/GlobalSearchFilter";

import styles from "./TypeSelector.module.scss";

const typeList: GlobalSearchFilter[] = [
  GlobalSearchFilter.ALL,
  GlobalSearchFilter.SAVED_LOCATIONS,
  GlobalSearchFilter.COMPANIES,
  GlobalSearchFilter.PEOPLE,
  GlobalSearchFilter.DEALS,
  GlobalSearchFilter.GROUPS,
  GlobalSearchFilter.ACTIVITIES,
  GlobalSearchFilter.ROUTES,
  GlobalSearchFilter.TERRITORIES,
  GlobalSearchFilter.RESOURCES,
  GlobalSearchFilter.USERS,
];

interface TypeSelectorProps {
  onSelect: (filter: GlobalSearchFilter) => void;
  savedLocationsEnabled: boolean;
  value: GlobalSearchFilter;
}

const TypeSelector = ({ onSelect, savedLocationsEnabled, value }: TypeSelectorProps) => {
  const intl = useIntl();
  const filters = useMemo(
    () =>
      savedLocationsEnabled
        ? typeList
        : typeList.filter((filter) => filter !== GlobalSearchFilter.SAVED_LOCATIONS),
    [savedLocationsEnabled]
  );

  const overflowRender = useCallback(
    (hiddenItemsCount: number) => {
      const overflownItems = filters.slice(-hiddenItemsCount);
      if (!overflownItems.length) {
        return null;
      }

      const menu: MenuProps = {
        items: overflownItems.map((type) => ({
          key: type,
          label: (
            <span className={styles.menuItem}>
              {getGlobalSearchFilterDisplayName(intl, type)}
              {value === type && <FontAwesomeIcon className={styles.checkIcon} icon={faCheck} />}
            </span>
          ),
          onClick: () => onSelect(type),
        })),
        selectable: true,
        selectedKeys: [value],
      };

      return (
        <Dropdown menu={menu}>
          <RoundButton
            active={overflownItems.includes(value)}
            icon={<FontAwesomeIcon icon={faEllipsisH} />}
          />
        </Dropdown>
      );
    },
    [filters, intl, onSelect, value]
  );

  return (
    <div className={styles.container}>
      <ListWithOverflowItem overflowRender={overflowRender}>
        {filters.map((filter) => (
          <RoundButton
            active={filter === value}
            className={styles.button}
            key={filter}
            onClick={() => onSelect(filter)}
          >
            {getGlobalSearchFilterDisplayName(intl, filter)}
          </RoundButton>
        ))}
      </ListWithOverflowItem>
    </div>
  );
};

export default TypeSelector;
